import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import {
  adImageUrl,
  adSourceUrl,
  api,
  cardArtUrl,
  fileToDataUrl,
  fmtDate,
  urlToDataUrl,
  type Ad,
  type CardHit,
} from '../api';
import { useData } from '../App';
import { DeleteButton, Empty, Icon, IconButton } from '../ui';
import { ApiError } from '../api';
import { TEMPLATES, templateById } from '../ads/templates';
import { withDefaults, type Field, type Template, type Values } from '../ads/types';
import { slug, usePoster } from '../ads/usePoster';

/* ══ AUGLÝSINGAR ═════════════════════════════════════════════════════
   A record of every poster the félag has put out, and an editor that
   builds a new one from a template. The poster is drawn on a canvas in
   the browser, so the preview here is literally the PNG that gets
   downloaded — see client/src/ads. */

/* Saving sends a couple of megabytes of PNG, so the ways it fails are
   worth naming — "it did not work" sends you to the server logs. */
const SAVE_ERRORS: Record<string, string> = {
  http_413: 'Auglýsingin er of stór til að vistast. Veldu minni mynd eða minnkaðu stækkunina.',
  payload_too_large: 'Auglýsingin er of stór til að vistast. Veldu minni mynd eða minnkaðu stækkunina.',
  title_required: 'Auglýsingin þarf heiti.',
  render_required: 'Plakatið teiknaðist ekki. Endurhlaðið síðuna og reyndu aftur.',
  bad_render: 'Plakatið teiknaðist ekki. Endurhlaðið síðuna og reyndu aftur.',
  bad_source: 'Myndin er á sniði sem við ráðum ekki við. Veldu aðra.',
  unauthorized: 'Innskráningin er útrunnin. Skráðu þig inn aftur.',
};

type Target =
  | { mode: 'new' }
  | { mode: 'edit'; ad: Ad }
  /** Same starting point as an existing ad, saved as a new row. */
  | { mode: 'copy'; ad: Ad };

export default function Auglysingar() {
  const { ads, refresh } = useData();
  const [target, setTarget] = useState<Target | null>(null);

  if (target) {
    return (
      <Editor
        key={target.mode + ('ad' in target ? target.ad.id : 'new')}
        target={target}
        onClose={() => setTarget(null)}
        onSaved={async () => {
          await refresh();
          setTarget(null);
        }}
      />
    );
  }

  return (
    <div className="view">
      <section className="card col-12">
        <div className="card-h">
          <h2>Auglýsingar</h2>
          <button type="button" className="btn sm" onClick={() => setTarget({ mode: 'new' })}>
            Ný auglýsing
          </button>
        </div>

        {ads.length === 0 ? (
          <Empty
            title="Engin auglýsing enn"
            body="Búðu til plakat fyrir næsta viðburð — veldu mynd, skrifaðu textann og sæktu PNG-skrá tilbúna á Facebook."
          />
        ) : (
          <div className="poster-grid">
            {ads.map((ad) => (
              <AdCard
                key={ad.id}
                ad={ad}
                onEdit={() => setTarget({ mode: 'edit', ad })}
                onCopy={() => setTarget({ mode: 'copy', ad })}
                onDeleted={refresh}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/* ── copy to clipboard ───────────────────────────────────────────── */

function CopyButton({
  text,
  label,
  className = 'btn ghost sm',
}: {
  text: string;
  label: string;
  className?: string;
}) {
  const [done, setDone] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // clipboard needs a secure context; fall back for plain http.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } finally {
        ta.remove();
      }
    }
    setDone(true);
    setTimeout(() => setDone(false), 1800);
  }

  return (
    <button type="button" className={className} onClick={() => void copy()} disabled={!text.trim()}>
      {done ? 'Afritað ✓' : label}
    </button>
  );
}

/* ── one poster in the archive ───────────────────────────────────── */

function AdCard({
  ad,
  onEdit,
  onCopy,
  onDeleted,
}: {
  ad: Ad;
  onEdit: () => void;
  onCopy: () => void;
  onDeleted: () => Promise<void>;
}) {
  const { events } = useData();
  const event = events.find((e) => e.id === ad.event_id);

  async function download() {
    // The stored PNG is the record of what was posted, so it is fetched
    // rather than redrawn — a template change must not rewrite history.
    const data = await urlToDataUrl(adImageUrl(ad.id, ad.updated_at));
    if (!data) return;
    const a = document.createElement('a');
    a.href = data;
    a.download = `${slug(ad.title)}.png`;
    a.click();
  }

  return (
    <figure className="poster-card">
      <button type="button" className="poster-thumb" onClick={onEdit} title="Opna og breyta">
        <img src={adImageUrl(ad.id, ad.updated_at)} alt={ad.title} loading="lazy" />
      </button>
      <figcaption>
        <b>{ad.title}</b>
        <span className="meta">
          {ad.posted_at ? fmtDate(ad.posted_at) : 'Óbirt'}
          {event ? ` · ${event.name}` : ''}
        </span>
        <span className="lbl">{templateById(ad.template).name}</span>
        {ad.post_text.trim() && (
          <details className="poster-post">
            <summary>Facebook-texti</summary>
            <p>{ad.post_text}</p>
            <CopyButton text={ad.post_text} label="Afrita texta" />
          </details>
        )}
        <div className="poster-acts">
          <IconButton label="Sækja PNG" onClick={() => void download()}>
            <Icon.download />
          </IconButton>
          <IconButton label="Breyta" onClick={onEdit}>
            <Icon.pencil />
          </IconButton>
          <IconButton label="Afrita í nýja" onClick={onCopy}>
            <Icon.copy />
          </IconButton>
          <DeleteButton
            label={`Eyða: ${ad.title}`}
            onClick={() => {
              void api.delAd(ad.id).then(onDeleted);
            }}
          />
        </div>
      </figcaption>
    </figure>
  );
}

/* ── the editor ──────────────────────────────────────────────────── */

function Editor({
  target,
  onClose,
  onSaved,
}: {
  target: Target;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { events } = useData();
  const existing = 'ad' in target ? target.ad : null;
  const isEdit = target.mode === 'edit';

  const [templateId, setTemplateId] = useState(existing?.template ?? TEMPLATES[0]!.id);
  const template: Template = useMemo(() => templateById(templateId), [templateId]);

  const [title, setTitle] = useState(
    existing ? (isEdit ? existing.title : `${existing.title} (afrit)`) : '',
  );
  const [values, setValues] = useState<Values>(() =>
    withDefaults(templateById(existing?.template ?? TEMPLATES[0]!.id), existing?.fields ?? {}),
  );
  const [eventId, setEventId] = useState<string>(String(existing?.event_id ?? ''));
  const [postedAt, setPostedAt] = useState(existing?.posted_at ?? '');
  const [credit, setCredit] = useState(existing?.credit ?? '');
  const [postText, setPostText] = useState(existing?.post_text ?? '');

  /* The picture may be a data URL just picked, or a URL on this server
     for an ad being reopened. `dirty` records whether it needs sending. */
  const [picture, setPicture] = useState<string | null>(
    existing?.has_source ? adSourceUrl(existing.id, existing.updated_at) : null,
  );
  const [pictureDirty, setPictureDirty] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { canvasRef, pictureFailed, missingFonts, fontsReady, toDataUrl, download } = usePoster(
    template,
    values,
    picture,
  );

  /* Switching template keeps anything the new one also asks for. */
  useEffect(() => {
    setValues((v) => withDefaults(template, v));
  }, [template]);

  const set = (key: string, value: string): void =>
    setValues((v) => ({ ...v, [key]: value }));

  function choosePicture(src: string | null): void {
    setPicture(src);
    setPictureDirty(true);
  }

  /** What to send as `source`: undefined keeps what the server has. */
  async function sourcePayload(): Promise<string | null | undefined> {
    if (isEdit && !pictureDirty) return undefined;
    if (!picture) return isEdit ? null : undefined;
    if (picture.startsWith('data:')) return picture;
    // A copy, or a picture picked from the card search: fetch the real
    // bytes rather than re-encoding what the canvas happens to hold.
    return (await urlToDataUrl(picture)) ?? undefined;
  }

  async function save() {
    if (!title.trim()) {
      setError('Auglýsingin þarf heiti.');
      return;
    }
    setSaving(true);
    setError('');
    /* Whether the server has it. Everything after this point — reloading the
       list, closing the editor — can fail on its own, and saying "it did not
       save" when it did sends someone off to make it a second time. */
    let stored = false;
    try {
      const render = toDataUrl();
      const payload = {
        title: title.trim(),
        template: template.id,
        fields: values,
        event_id: eventId ? Number(eventId) : null,
        credit: credit.trim(),
        post_text: postText,
        posted_at: postedAt || null,
        render,
        source: await sourcePayload(),
      };
      if (isEdit && existing) await api.updateAd(existing.id, payload);
      else await api.addAd(payload);
      stored = true;
      await onSaved();
    } catch (err) {
      if (stored) {
        setError('Auglýsingin er vistuð, en listinn náði ekki að uppfærast. Endurhlaðið síðuna til að sjá hana.');
      } else {
        const code = err instanceof ApiError ? err.code : 'network';
        setError(SAVE_ERRORS[code] ?? `Ekki tókst að vista auglýsinguna (${code}).`);
      }
    } finally {
      setSaving(false);
    }
  }

  const needsPicture = template.usesPicture && !picture;

  return (
    <div className="view">
      <div className="g-two">
        <section className="card col-5 poster-form">
          <div className="card-h">
            <h2>{isEdit ? 'Breyta auglýsingu' : 'Ný auglýsing'}</h2>
            <button type="button" className="btn ghost sm" onClick={onClose}>
              Til baka
            </button>
          </div>

          {error && (
            <div className="alert" role="alert">
              {error}
            </div>
          )}

          <div className="form">
            <div className="fld">
              <label className="lbl" htmlFor="poster-title">
                Heiti (aðeins fyrir stjórnborðið)
              </label>
              <input
                id="poster-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Reality Fracture sealed — okt 2026"
                required
              />
            </div>

            {TEMPLATES.length > 1 && (
              <div className="fld">
                <label className="lbl" htmlFor="poster-tpl">
                  Snið
                </label>
                <select id="poster-tpl" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                  {TEMPLATES.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                <span className="hint">{template.blurb}</span>
              </div>
            )}

            {template.usesPicture && (
              <PicturePicker
                picture={picture}
                onPick={choosePicture}
                onCardPicked={(hit) => {
                  if (!credit.trim()) setCredit(hit.credit);
                  set('credit', `${hit.name}\nlist: ${hit.artist}`);
                  if (!values.subhead?.trim()) set('subhead', hit.setName);
                }}
              />
            )}

            {template.groups.map((g) => (
              <fieldset className="poster-group" key={g.title}>
                <legend>{g.title}</legend>
                <div className="poster-fields">
                  {g.fields.map((f) => (
                    <FieldInput key={f.key} field={f} value={values[f.key] ?? ''} onChange={set} />
                  ))}
                </div>
              </fieldset>
            ))}

            <fieldset className="poster-group">
              <legend>Facebook-færslan</legend>
              <div className="poster-fields">
                <div className="fld">
                  <label className="lbl" htmlFor="poster-post">
                    Textinn sem fylgir myndinni
                  </label>
                  <textarea
                    id="poster-post"
                    rows={8}
                    value={postText}
                    onChange={(e) => setPostText(e.target.value)}
                    placeholder={
                      'Sealed-mót í Reality Fracture laugardaginn 3. október!\n\n' +
                      'Þú færð sex pakka, byggir 40 spila stokk og spilar þrjár umferðir.\n' +
                      'Byrjendur velkomnir — við hjálpum þér af stað.\n\n' +
                      'Hvar: Edinborgarhúsið\nVerð: 6.000 kr.\nPláss: 12'
                    }
                  />
                  <span className="hint">
                    Þetta fer <b>ekki</b> á plakatið — þetta er textinn sem þú límir inn í
                    Facebook-færsluna sjálfa. Facebook styttir í „sjá meira“ eftir um 400 stafi, svo
                    hafðu það sem mestu skiptir efst.
                  </span>
                </div>
                <div className="row poster-post-foot">
                  <span className={`lbl${postText.length > 400 ? ' over' : ''}`}>
                    {postText.length} stafir
                    {postText.length > 400 ? ` · ${postText.length - 400} fram yfir „sjá meira“` : ''}
                  </span>
                  <CopyButton text={postText} label="Afrita texta" />
                </div>
              </div>
            </fieldset>

            <fieldset className="poster-group">
              <legend>Skráning</legend>
              <div className="poster-fields">
                <div className="fld half">
                  <label className="lbl" htmlFor="poster-event">
                    Tengja viðburði
                  </label>
                  <select id="poster-event" value={eventId} onChange={(e) => setEventId(e.target.value)}>
                    <option value="">Enginn</option>
                    {events.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="fld half">
                  <label className="lbl" htmlFor="poster-posted">
                    Birt þann
                  </label>
                  <input
                    id="poster-posted"
                    type="date"
                    value={postedAt}
                    onChange={(e) => setPostedAt(e.target.value)}
                  />
                  <span className="hint">Skildu eftir autt á meðan hún er í vinnslu.</span>
                </div>
                <div className="fld">
                  <label className="lbl" htmlFor="poster-credit">
                    Hvaðan kom myndin
                  </label>
                  <input
                    id="poster-credit"
                    type="text"
                    value={credit}
                    onChange={(e) => setCredit(e.target.value)}
                    placeholder="Spil, listamaður eða ljósmyndari"
                  />
                  <span className="hint">
                    Geymt með auglýsingunni — ekki teiknað á plakatið. Smáaletrið hér að ofan er það
                    sem sést.
                  </span>
                </div>
              </div>
            </fieldset>
          </div>
        </section>

        <section className="card col-7 poster-preview">
          <div className="card-h">
            <h2>Forskoðun</h2>
            <span className="lbl">
              {template.width}×{template.height}
            </span>
          </div>

          <div className="poster-stage">
            <canvas
              ref={canvasRef}
              width={template.width}
              height={template.height}
              aria-label="Forskoðun auglýsingar"
            />
            {!fontsReady && <div className="poster-veil">Sæki letur…</div>}
          </div>

          {missingFonts.length > 0 && (
            <div className="alert" role="alert">
              Letrið hlóðst ekki ({missingFonts.join(', ')}). Plakatið teiknast með varaletri og
              lítur rangt út — athugaðu nettenginguna og endurhlaðið síðuna áður en þú vistar.
            </div>
          )}
          {pictureFailed && (
            <div className="alert" role="alert">
              Myndin hlóðst ekki. Veldu aðra.
            </div>
          )}
          {needsPicture && !pictureFailed && (
            <div className="alert warn" role="status">
              Engin mynd valin — efri hluti plakatsins verður svartur. Leitaðu að spili eða
              hlaðið upp mynd hér til vinstri.
            </div>
          )}

          <div className="row poster-actions">
            <button
              type="button"
              className="btn ghost"
              onClick={() => download(`${slug(title || template.name)}.png`)}
              disabled={!fontsReady}
            >
              <Icon.download /> Sækja PNG
            </button>
            <button type="button" className="btn" onClick={() => void save()} disabled={saving || !fontsReady}>
              {saving
                ? 'Vista…'
                : needsPicture
                  ? 'Vista án myndar'
                  : isEdit
                    ? 'Vista breytingar'
                    : 'Vista auglýsingu'}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

/* ── a single field, built from the template's own description ───── */

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: Field;
  value: string;
  onChange: (key: string, value: string) => void;
}) {
  const cls = `fld${field.width === 'half' ? ' half' : ''}`;
  const id = `f-${field.key}`;

  if (field.kind === 'toggle') {
    return (
      <label className={`${cls} poster-toggle`} htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={value === '1'}
          onChange={(e) => onChange(field.key, e.target.checked ? '1' : '')}
        />
        <span>{field.label}</span>
      </label>
    );
  }

  if (field.kind === 'range') {
    const n = Number(value) || field.min;
    return (
      <div className={cls}>
        <label className="lbl" htmlFor={id}>
          {field.label} <span className="mono-n">{n}{field.suffix ?? ''}</span>
        </label>
        <input
          id={id}
          type="range"
          min={field.min}
          max={field.max}
          step={field.step ?? 1}
          value={n}
          onChange={(e) => onChange(field.key, e.target.value)}
        />
      </div>
    );
  }

  if (field.kind === 'lines') {
    return (
      <div className={cls}>
        <label className="lbl" htmlFor={id}>
          {field.label}
        </label>
        <textarea
          id={id}
          rows={field.rows ?? 2}
          value={value}
          onChange={(e) => onChange(field.key, e.target.value)}
        />
        {field.hint && <span className="hint">{field.hint}</span>}
      </div>
    );
  }

  return (
    <div className={cls}>
      <label className="lbl" htmlFor={id}>
        {field.label}
      </label>
      <input
        id={id}
        type="text"
        maxLength={field.max ?? 120}
        value={value}
        onChange={(e) => onChange(field.key, e.target.value)}
      />
      {field.hint && <span className="hint">{field.hint}</span>}
    </div>
  );
}

/* ── choosing the picture ────────────────────────────────────────── */

function PicturePicker({
  picture,
  onPick,
  onCardPicked,
}: {
  picture: string | null;
  onPick: (src: string | null) => void;
  onCardPicked: (hit: CardHit) => void;
}) {
  const [tab, setTab] = useState<'card' | 'file'>('card');
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<CardHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [fileError, setFileError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  /* Typing settles before anything is asked of Scryfall — they ask for
     one request at a time, not one per keystroke. */
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setHits([]);
      setSearched(false);
      return;
    }
    const t = setTimeout(() => {
      setSearching(true);
      api
        .cardSearch(term)
        .then(setHits)
        .catch(() => setHits([]))
        .finally(() => {
          setSearching(false);
          setSearched(true);
        });
    }, 450);
    return () => clearTimeout(t);
  }, [q]);

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = await fileToDataUrl(file);
    if (!data) {
      setFileError('Myndin er of stór (hámark 10 MB) eða ekki á myndsniði.');
      return;
    }
    setFileError('');
    onPick(data);
  }

  return (
    <fieldset className="poster-group">
      <legend>Mynd</legend>

      <div className="filters">
        <button
          type="button"
          className={`filt${tab === 'card' ? ' on' : ''}`}
          aria-pressed={tab === 'card'}
          onClick={() => setTab('card')}
        >
          <Icon.search /> Leita í spilum
        </button>
        <button
          type="button"
          className={`filt${tab === 'file' ? ' on' : ''}`}
          aria-pressed={tab === 'file'}
          onClick={() => setTab('file')}
        >
          <Icon.picture /> Hlaða upp
        </button>
      </div>

      {tab === 'card' ? (
        <>
          <div className="fld">
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nafn á spili — t.d. Jace Beleren"
              aria-label="Leita að spili"
            />
            <span className="hint">
              Myndir koma frá Scryfall og eru geymdar hjá okkur eftir fyrsta sókn.
            </span>
          </div>

          {searching && <p className="note-empty">Leita…</p>}
          {!searching && searched && hits.length === 0 && (
            <p className="note-empty">Ekkert spil fannst.</p>
          )}

          {hits.length > 0 && (
            <div className="card-hits">
              {hits.map((h) => (
                <button
                  key={h.id}
                  type="button"
                  className="card-hit"
                  onClick={() => {
                    onPick(cardArtUrl(h.id));
                    onCardPicked(h);
                  }}
                  title={`${h.name} — ${h.artist}`}
                >
                  <img src={cardArtUrl(h.id)} alt="" loading="lazy" />
                  <span>
                    <b>{h.name}</b>
                    <i>
                      {h.setCode} · {h.artist}
                    </i>
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="fld">
          <input ref={fileRef} type="file" accept="image/*" onChange={(e) => void onFile(e)} />
          <span className="hint">
            Breiðar myndir virka best. Myndin er skalað upp í 1080 px, svo lágupplausn verður
            óskýr.
          </span>
          {fileError && <span className="hint bad">{fileError}</span>}
        </div>
      )}

      {picture && (
        <div className="row poster-chosen">
          <img src={picture} alt="Valin mynd" />
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => {
              onPick(null);
              if (fileRef.current) fileRef.current.value = '';
            }}
          >
            Fjarlægja mynd
          </button>
        </div>
      )}
    </fieldset>
  );
}
